import { useRef, useState } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { v4 as uuidv4 } from 'uuid';
import '@/shared/quiz/events.quiz-match';
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { type CreateQuizSetReq, getGetQuizSetBuildInfoQueryKey, getGetQuizzesDetailStatsQueryKey, getGetQuizzesStatsQueryKey, type UpdateQuizSetReq, useCreateQuizSet, useEditQuizSet, ZepQuizApiError } from '@/api';
import { MY_QUIZ_LIST_QUERY_KEY, QUIZ_DETAIL_QUERY_KEY } from '@/api/quiz';
import { useAlert } from '@/hooks/useAlert';
import { CheckSmallIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { useCreateAssignmentDialogEffect, useIsInGoogleClassroom } from '@/shared/googleClassroom';
import { ltiContextManager } from '@/shared/lti';
import { useUserPlanManager } from '@/shared/plan';
import { type PlanType } from '@/types';
import { alertDialog, Button } from '@/ui_v3';
import { createElapsedTimeLogger } from '@/utils/createElapsedTimeLogger';
import { useBuildEventTrack } from '@/views/build/action/useBuildEventTrack';
import { useBuildValidate } from '@/views/build/action/useBuildValidate';
import { focusField } from '@/views/build/components/focus/focusUtils';
import { useBuildInfoContext } from '@/views/build/context';
import { type BuildSchemaInputType } from '@/views/build/form/schema';
import { usePersistBuildForm } from '@/views/build/hook';
import { DisabledEditAlertModal } from '@/views/build/modal';
import { useQuizTitle } from '../features/build-quiz-title';
import { useBuildFormContext } from '../form/schema';
import { transformBuildFormToApiRequest } from '../util/buildFormToApiTransformer';
import { getBuildType } from '../util/getBuildType';
import { resolveMapTemplateAvailability } from '../utils/resolveMapTemplateAvailability';
import { resolveRestrictedMatchQuizIndexes } from '../utils/resolveRestrictedQuizContent';
import { useValidateQuizList } from './useValidateQuizList';
export const BuildHeaderSubmitButton = ({
  onSubmitQuiz,
  onSubmittingChange
}: {
  onSubmitQuiz: () => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}) => {
  const {
    validateAll
  } = useBuildValidate();
  const alertModal = useAlert();
  const {
    action,
    canEditQuiz,
    originQuizSet,
    mapTemplates
  } = useBuildInfoContext();
  const {
    titlePlaceholder
  } = useQuizTitle();
  const onSuccessTrack = useBuildEventTrack();
  const {
    user,
    updateUser
  } = useUser();
  const {
    userPlanManager
  } = useUserPlanManager();
  const isUpdated = action === 'UPDATE';
  const isDuplicate = action === 'DUPLICATE';
  const getSubmitAction = () => {
    if (isUpdated) return 'UPDATE';
    if (isDuplicate) return 'DUPLICATE';
    return 'CREATE';
  };
  const {
    getValues,
    reset
  } = useBuildFormContext();
  const disabledEditAlertModal = useModal(DisabledEditAlertModal);
  const client = useQueryClient();
  const router = useRouter();
  const {
    t
  } = useTranslation(['build', 'common']);
  const {
    clearPersist
  } = usePersistBuildForm();
  const validateQuizList = useValidateQuizList();
  const getMatchSaveProps = (formData: BuildSchemaInputType) => {
    const matchQuestionCount = formData.quizList.filter(quiz => quiz.type === 'match').length;
    return {
      build_type: getBuildType(action),
      has_match_question: matchQuestionCount > 0,
      match_question_count: matchQuestionCount
    };
  };
  const trackSaveAttempt = (saveAttemptId: string, formData: BuildSchemaInputType) => {
    trackEvent('quiz_build_question_save_attempt', {
      save_attempt_id: saveAttemptId,
      ...getMatchSaveProps(formData)
    });
  };
  const trackSaveResult = (saveAttemptId: string, formData: BuildSchemaInputType, result: 'success' | 'validation_fail' | 'api_fail', failReason?: string) => {
    trackEvent('quiz_build_question_save', {
      save_attempt_id: saveAttemptId,
      ...getMatchSaveProps(formData),
      result,
      ...(failReason ? {
        fail_reason: failReason
      } : {})
    });
  };

  // 저장 이중 제출의 기계적 버그(confirm 1클릭에 mutate 2회 발사)만 막는 최소 가드.
  // 더블클릭 시 useAlert 가 NiceModal 공유 프로미스에 .then 을 2중 등록해 confirm
  // 콜백이 2번 발화할 수 있으므로, mutate 실행 직전에 재진입을 차단하고 완료 시 해제한다.
  // 버튼 disabled 나 모달 dismiss 동작은 건드리지 않아 기존 UX 와 동일하다.
  const submitInFlightRef = useRef(false);

  // 저장 요청이 실제로 진행 중인 동안(=submitInFlightRef 와 동일 수명)만 true.
  // 부모(BuildPageView)는 이 값으로 빌더 전체를 inert 처리해 편집을 막는다.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateSubmitting = (value: boolean) => {
    setIsSubmitting(value);
    onSubmittingChange?.(value);
  };
  const cleanIsDirty = () => {
    reset({
      ...getValues()
    }, {
      keepDirty: false
    });
  };
  const showSaveBlockedAlert = () => {
    focusField('templateId');
    void alertModal({
      type: 'alert',
      title: t('build:build.lockRecovery.saveBlockedTitle'),
      content: t('build:build.lockRecovery.saveBlockedDescription')
    });
  };
  const showRestrictedMatchSaveBlockedAlert = (quizIndex?: number) => {
    void disabledEditAlertModal.show({
      type: 'plan',
      recoveryReasons: ['match'],
      onEdit: () => {
        if (quizIndex !== undefined) {
          focusField(`quizList.${quizIndex}.question`);
        }
      }
    });
  };
  const onErrorAction = (error: unknown, saveAttemptId: string, formData: BuildSchemaInputType, shouldTrackResult = true) => {
    console.error('buildError', error);
    if (shouldTrackResult) {
      trackSaveResult(saveAttemptId, formData, 'api_fail', error instanceof ZepQuizApiError ? error.status : 'unknown');
    }
    if (error instanceof ZepQuizApiError) {
      if (error.status === 'CAN_NOT_USE_PREMIUM_MAP_TEMPLATE') {
        showSaveBlockedAlert();
        return;
      }
      if (error.status === 'CAN_NOT_USE_PREMIUM_QUIZ_TYPE') {
        const [firstMatchQuizIndex] = resolveRestrictedMatchQuizIndexes({
          quizList: getValues().quizList,
          // The server's rejection is authoritative even when locally cached
          // entitlement data is stale.
          hasMatchAccess: false
        });
        showRestrictedMatchSaveBlockedAlert(firstMatchQuizIndex);
        return;
      }
    }

    // ux
    setTimeout(() => {
      const track = (btn: 'build' | 'home') => {
        logger.track('create_fail', {
          btn
        });
      };
      void alertModal({
        useOutSideClick: false,
        type: 'confirm',
        title: t('build:build.actionError.occurredBuildQuiz'),
        cancelText: t('build:build.actionError.goHome'),
        cancelAction: () => {
          track('home');
          void router.push('/public');
        },
        content: t('build:build.actionError.waitTry')
      }).then(() => {
        track('build');
        router.reload();
      });
    }, 1000);
  };
  const invalidateQuery = async (quizsetId?: string) => {
    const invalidateTargets = [client.invalidateQueries({
      queryKey: QUIZ_DETAIL_QUERY_KEY
    }), client.invalidateQueries({
      queryKey: MY_QUIZ_LIST_QUERY_KEY
    }), client.invalidateQueries({
      queryKey: ['quizsets/search']
    }), client.invalidateQueries({
      queryKey: getGetQuizSetBuildInfoQueryKey()
    })];
    if (quizsetId) {
      invalidateTargets.push(client.invalidateQueries({
        queryKey: getGetQuizzesStatsQueryKey({
          quizsetId
        })
      }), client.invalidateQueries({
        queryKey: getGetQuizzesDetailStatsQueryKey({
          quizsetId
        })
      }));
    }
    return Promise.all(invalidateTargets);
  };
  const isInGoogleClassroom = useIsInGoogleClassroom();
  const {
    mutateAsync: createMutate
  } = useCreateQuizSet();
  const {
    mutateAsync: updateMutate
  } = useEditQuizSet();
  const getBuildDurationLogger = (buildStartTime: number) => {
    const logMutationElapsedTime = createElapsedTimeLogger({
      startTime: buildStartTime,
      enableLog: process.env.NODE_ENV === 'development' ? true : false,
      enableToast: false
    });
    const logBuildPageExitElapsedTime = createElapsedTimeLogger({
      startTime: buildStartTime,
      enableLog: process.env.NODE_ENV === 'development' ? true : false,
      enableToast: false
    });
    return {
      logMutationElapsedTime,
      logBuildPageExitElapsedTime
    };
  };
  const handleCreate = async (data: CreateQuizSetReq, formData: BuildSchemaInputType, saveAttemptId: string) => {
    const isValid = await validateQuizList();
    if (!isValid) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'quiz_list_validation');
      return;
    }

    // 이중 실행 가드: 저장이 진행 중이면 두 번째 호출은 무시한다.
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;
    updateSubmitting(true);
    let saveResultTracked = false;
    try {
      const buildStartTime = new Date().getTime();
      const {
        logMutationElapsedTime,
        logBuildPageExitElapsedTime
      } = getBuildDurationLogger(buildStartTime);
      const result = await createMutate({
        data
      });
      trackSaveResult(saveAttemptId, formData, 'success');
      saveResultTracked = true;
      onSubmitQuiz();
      cleanIsDirty();
      const mutationCompleteDuration = logMutationElapsedTime();
      const {
        categoryTreeData
      } = result.data;
      //빌드 성공 시, 유저의 최근 퀴즈 정보 업데이트
      updateUser({
        recentQuizsetInfo: {
          createdAt: result.data.createdAt,
          schoolGrade: categoryTreeData?.schoolGrade,
          schoolSemester: categoryTreeData?.schoolSemester,
          schoolType: categoryTreeData?.schoolType
        }
      });
      void invalidateQuery(result.data.id).then(async () => {
        clearPersist();
        if (ltiContextManager.isInLti()) {
          await router.push(`/quiz/${result.data.id}`);
        } else {
          await router.push({
            pathname: `/quiz/${result.data.id}`,
            query: {
              [useCreateAssignmentDialogEffect.KEY]: isInGoogleClassroom,
              isDuplicate
            }
          });
        }
        onSuccessTrack(result.data.id, result.data?.countryCode || '', {
          // 만들기 시 걸린 시간 측정 - create 완료 / 퀴즈 상세 페이지 이동 시간 분리 로깅
          mutationCompleteDuration,
          buildPageExitDuration: logBuildPageExitElapsedTime()
        });
      });
    } catch (error) {
      onErrorAction(error, saveAttemptId, formData, !saveResultTracked);
    } finally {
      submitInFlightRef.current = false;
      updateSubmitting(false);
    }
  };
  const handleUpdate = async (data: UpdateQuizSetReq, formData: BuildSchemaInputType, saveAttemptId: string) => {
    const isValid = await validateQuizList();
    if (!isValid) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'quiz_list_validation');
      return;
    }
    const updateStartTime = new Date().getTime();
    const {
      logMutationElapsedTime,
      logBuildPageExitElapsedTime
    } = getBuildDurationLogger(updateStartTime);
    void alertModal({
      type: 'confirm',
      title: t('update.alert.refreshMessage')
    }, () => {
      void (async () => {
        // 더블클릭 시 useAlert 가 NiceModal 공유 프로미스에 .then 을 2중 등록해
        // 이 콜백이 2번 발화할 수 있다. mutate 직전 재진입 가드로 저장은 1회만
        // 실행한다. (dismiss 로 닫으면 이 콜백은 발화하지 않아 가드가 남지 않는다.)
        if (submitInFlightRef.current) {
          return;
        }
        submitInFlightRef.current = true;
        updateSubmitting(true);
        let saveResultTracked = false;
        try {
          const result = await updateMutate({
            data
          });
          trackSaveResult(saveAttemptId, formData, 'success');
          saveResultTracked = true;
          void onSubmitQuiz();
          cleanIsDirty();
          const mutationCompleteDuration = logMutationElapsedTime();
          await invalidateQuery(result.data.id);
          if (ltiContextManager.isInLti()) {
            await router.push('/myquiz');
          } else {
            await router.push(`/quiz/${result.data.id}`);
          }
          onSuccessTrack(result.data?.id, result.data?.countryCode || '', {
            mutationCompleteDuration,
            buildPageExitDuration: logBuildPageExitElapsedTime()
          });
        } catch (error) {
          onErrorAction(error, saveAttemptId, formData, !saveResultTracked);
        } finally {
          submitInFlightRef.current = false;
          updateSubmitting(false);
        }
      })();
    });
  };
  const onSubmit = async () => {
    const formData = getValues();
    const saveAttemptId = uuidv4();
    trackSaveAttempt(saveAttemptId, formData);

    // Guest user guard - require login to save quiz
    if (!user) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'unauthenticated');
      await alertModal({
        type: 'alert',
        title: t('build:alert.guestLoginRequired.title'),
        content: t('build:alert.guestLoginRequired.description')
      }).then(() => {
        // Redirect to login with callback URL
        void router.push(`/login?callbackUrl=${encodeURIComponent(router.asPath)}`);
      });
      return;
    }
    if (!canEditQuiz) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'not_editable');
      void disabledEditAlertModal.show();
      return;
    }
    if (!validateAll()) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'build_validation');
      return;
    }
    const [firstRestrictedMatchQuizIndex] = resolveRestrictedMatchQuizIndexes({
      quizList: formData.quizList,
      hasMatchAccess: userPlanManager.hasEnoughPlanType('PRO')
    });
    if (firstRestrictedMatchQuizIndex !== undefined) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'match_plan_restricted');
      showRestrictedMatchSaveBlockedAlert(firstRestrictedMatchQuizIndex);
      return;
    }
    const mapTemplateAvailability = resolveMapTemplateAvailability({
      templateId: formData.templateId,
      visibleMapTemplates: mapTemplates,
      originMapTemplate: originQuizSet,
      hasEnoughPlanType: planType => userPlanManager.hasEnoughPlanType(planType as PlanType)
    });
    if (mapTemplateAvailability === 'unavailable-visible' || mapTemplateAvailability === 'unavailable-original' || mapTemplateAvailability === 'unknown') {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'map_template_unavailable');
      showSaveBlockedAlert();
      return;
    }
    if (!formData.title && !titlePlaceholder) {
      trackSaveResult(saveAttemptId, formData, 'validation_fail', 'title_required');
      // title 혹은 titlePlaceholder 중 하나는 반드시 존재해야 합니다.
      logger.debug('unexpected_title_empty', {
        title: formData.title,
        titlePlaceholder
      });
      alertDialog.open({
        header: {
          title: t('build:build.alert.title')
        }
      });
      return;
    }
    const upsertData = transformBuildFormToApiRequest({
      ...formData,
      title: formData.title || titlePlaceholder || ''
    }, {
      action: getSubmitAction(),
      quizSetId: String(router.query.id),
      originQuizSetId: isDuplicate ? originQuizSet?.originQuizSetId : undefined
    });
    if (isUpdated) {
      await handleUpdate({
        ...upsertData,
        quizSetId: router.query.id?.toString() || ''
      }, formData, saveAttemptId);
      return;
    }
    const {
      quizList: _createdQuizes,
      ...createPayload
    } = upsertData;
    await handleCreate({
      ...createPayload,
      title: String(createPayload.title),
      quizList: _createdQuizes?.map(q => ({
        ...q,
        hint: {
          ...q.hint
        }
      })) || []
    }, formData, saveAttemptId);
  };
  return <Button size="md" disabled={isSubmitting} onClick={onSubmit} leftSlot={<CheckSmallIcon size="sm" />} data-sentry-element="Button" data-sentry-component="BuildHeaderSubmitButton" data-sentry-source-file="BuildHeaderSubmitButton.tsx">
      <span className="hidden mobile-lg:inline-block">
        {isUpdated ? t('build:update.complete.button') : t('build:build.complete.button')}
      </span>
      <span className="inline-block mobile-lg:hidden">
        {t('common:global.complete.button')}
      </span>
    </Button>;
};